/**
 * AuditQuotaView - Roland Garros uniquement
 * Vérifie qu'aucun email_compte n'a plus de 4 billets ACHAT pour le même match (evenement + date_evenement).
 * Règle FFT/RG : max 4 places par compte / par session.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, ShieldCheck, Download, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const QUOTA_MAX = 4;

const AuditQuotaView = ({ tickets = [] }) => {
  const [showOnlyViolations, setShowOnlyViolations] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);

  const groups = useMemo(() => {
    // Group ACHAT par email_compte + evenement + date_evenement
    const map = {};
    for (const t of tickets) {
      if (t.type !== 'ACHAT') continue;
      const email = (t.email_compte || '').trim().toLowerCase();
      if (!email) continue;
      const ev = (t.evenement || '').trim();
      const date = (t.date_evenement || '').trim();
      const key = `${email}|||${ev}|||${date}`;
      if (!map[key]) {
        map[key] = { email, evenement: ev, date, tickets: [] };
      }
      map[key].tickets.push(t);
    }
    return Object.values(map).map(g => ({
      ...g,
      count: g.tickets.length,
      isViolation: g.tickets.length > QUOTA_MAX,
    })).sort((a, b) => {
      // Violations d'abord, puis comptes (les plus chargés en premier), puis match
      if (a.isViolation !== b.isViolation) return a.isViolation ? -1 : 1;
      if (a.count !== b.count) return b.count - a.count;
      if (a.email !== b.email) return a.email.localeCompare(b.email);
      return a.evenement.localeCompare(b.evenement);
    });
  }, [tickets]);

  const violations = useMemo(() => groups.filter(g => g.isViolation), [groups]);
  const displayed = showOnlyViolations ? violations : groups;

  const toggleCollapse = (k) => setCollapsed(prev => ({ ...prev, [k]: !prev[k] }));

  const doCopy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
    toast.success('Copié !');
  };

  const exportCSV = () => {
    const rows = [['Email Compte', 'Match', 'Date', 'Nb Billets', 'Excédent', 'Statut', 'Refs Achat']];
    for (const g of displayed) {
      rows.push([
        g.email,
        g.evenement,
        g.date,
        g.count,
        Math.max(0, g.count - QUOTA_MAX),
        g.isViolation ? 'DÉPASSEMENT' : 'OK',
        g.tickets.map(t => t.ref_achat || '').filter(Boolean).join(' ; '),
      ]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_quota_rg_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exporté !');
  };

  const violationCount = violations.length;
  const totalExcess = violations.reduce((s, g) => s + (g.count - QUOTA_MAX), 0);

  return (
    <div className="space-y-4" data-testid="audit-quota-view">
      {/* Header */}
      <div className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-3 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {violationCount > 0 ? (
            <AlertTriangle className="h-6 w-6 text-red-400" />
          ) : (
            <ShieldCheck className="h-6 w-6 text-emerald-400" />
          )}
          <div>
            <div className="text-sm text-white font-semibold">
              Audit Quota Roland Garros · max {QUOTA_MAX} billets / compte / match
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {violationCount === 0
                ? `Aucun dépassement détecté · ${groups.length} couple(s) email/match analysé(s)`
                : <>
                    <strong className="text-red-400">{violationCount}</strong> compte(s) en dépassement ·
                    <strong className="text-red-400 ml-1">{totalExcess}</strong> billet(s) au-dessus du quota
                  </>
              }
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowOnlyViolations(!showOnlyViolations)}
            className={cn(
              "px-3 py-1.5 text-xs font-bold rounded border transition-all",
              showOnlyViolations
                ? "bg-red-600 hover:bg-red-500 text-white border-red-400"
                : "bg-zinc-700 hover:bg-zinc-600 text-zinc-200 border-zinc-600"
            )}
            data-testid="toggle-violations-only"
          >
            {showOnlyViolations ? `⚠ DÉPASSEMENTS UNIQUEMENT (${violationCount})` : `TOUS (${groups.length})`}
          </button>
          <Button size="sm" onClick={exportCSV} className="bg-[#C4A052] hover:bg-[#D4B86A] text-black" data-testid="export-audit-csv">
            <Download className="h-4 w-4 mr-1" /> Exporter CSV
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {displayed.length === 0 && (
        <div className="text-center py-12 text-zinc-500">
          <ShieldCheck className="h-12 w-12 mx-auto mb-3 text-emerald-400/60" />
          <p className="text-lg">
            {showOnlyViolations
              ? 'Aucun dépassement de quota détecté ! 🎉'
              : 'Aucun billet ACHAT avec email_compte renseigné.'}
          </p>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-1.5">
        {displayed.map((g) => {
          const key = `${g.email}|${g.evenement}|${g.date}`;
          const isOpen = !collapsed[key];
          const excess = Math.max(0, g.count - QUOTA_MAX);
          return (
            <div
              key={key}
              className={cn(
                "rounded-lg border overflow-hidden",
                g.isViolation
                  ? "bg-red-950/40 border-red-500/50"
                  : "bg-zinc-800/40 border-zinc-700"
              )}
              data-testid={`audit-row-${key}`}
            >
              <button
                onClick={() => toggleCollapse(key)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-400 flex-shrink-0" />}
                <span className={cn(
                  "px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide",
                  g.isViolation ? "bg-red-600 text-white" : "bg-emerald-700/60 text-emerald-100"
                )}>
                  {g.isViolation ? `+${excess} EXCÈS` : 'OK'}
                </span>
                <span className={cn(
                  "font-mono text-sm font-bold min-w-[60px] text-center",
                  g.isViolation ? "text-red-300" : "text-zinc-300"
                )}>
                  {g.count}/{QUOTA_MAX}
                </span>
                <span className="text-sm text-white font-medium truncate flex-1 min-w-0">{g.email}</span>
                <span className="text-xs text-zinc-400 truncate max-w-[300px] hidden md:inline">{g.evenement || '—'}</span>
                {g.date && <span className="text-xs text-zinc-500 hidden md:inline">{g.date}</span>}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-1 text-zinc-500 hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    const text = `${g.email} | ${g.evenement} | ${g.date} | ${g.count} billets${g.isViolation ? ` (excès: ${excess})` : ''}\n` +
                      g.tickets.map(t => `  ${t.ref_achat || '?'} · cat:${t.categorie || ''} · B:${t.block || ''} R:${t.rang || ''} P:${t.place || ''} · ${t.source || ''}`).join('\n');
                    doCopy(key, text);
                  }}
                >
                  {copiedKey === key ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </button>

              {isOpen && (
                <div className="border-t border-white/5 bg-black/20 px-4 py-2 space-y-1">
                  {g.tickets.map((t, i) => (
                    <div key={t.id || i} className="grid grid-cols-12 gap-2 text-xs items-center py-1 border-b border-white/5 last:border-b-0">
                      <span className="col-span-2 font-mono text-[#C4A052] truncate">{t.ref_achat || '—'}</span>
                      <span className="col-span-2 text-zinc-300 truncate">{t.categorie || '—'}</span>
                      <span className="col-span-1 text-zinc-300">B:{t.block || '—'}</span>
                      <span className="col-span-1 text-zinc-300">R:{t.rang || '—'}</span>
                      <span className="col-span-1 text-zinc-300">P:{t.place || '—'}</span>
                      <span className="col-span-2 text-zinc-400 truncate">{t.source || '—'}</span>
                      <span className={cn("col-span-2 text-xs px-1.5 py-0.5 rounded inline-block text-center",
                        t.attribue ? "bg-emerald-700/40 text-emerald-200" : "bg-zinc-700/60 text-zinc-300"
                      )}>
                        {t.attribue ? 'attribué' : 'non-attribué'}
                      </span>
                      <span className="col-span-1 text-right">
                        {i + 1 > QUOTA_MAX && <span className="text-red-400 font-bold">⚠</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuditQuotaView;
